import { PropsWithChildren, useCallback, useEffect } from "react";
import { FieldPath, FieldValues, get, useFormContext } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";

export interface PartialFormProps<T extends FieldValues>
  extends PropsWithChildren {
  onSubmit(values: T): void;
  trigger?: FieldPath<T>[] | FieldPath<T>;
}

export const PartialForm = <T extends FieldValues>({
  onSubmit,
  children,
  trigger = [],
}: PartialFormProps<T>) => {
  const form = useFormContext<T>();

  const validate = useCallback(
    (keys?: FieldPath<T>[]) => {
      if (keys) {
        return form.trigger(keys);
      }

      const triggerValues = Array.from(
        form.control._names.mount.values(),
      ).filter(
        (key) => get(form.control._fields, key)?._f.mount,
      ) as FieldPath<T>[];

      return form.trigger(triggerValues.concat(trigger));
    },
    [form],
  );

  const debounceValidate = useDebouncedCallback(validate, 1000);

  // Effect to subscribe to form changes.
  useEffect(() => {
    const subscription = form.control._subjects.values.subscribe({
      next: () => {
        const keys = Object.keys(form.formState.errors);
        if (Object.keys(form.formState.errors).length) {
          debounceValidate(keys as FieldPath<T>[]);
        }
      },
    });

    return () => subscription.unsubscribe();
  }, [form, form.formState.isSubmitted, debounceValidate]);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (await validate()) {
          onSubmit(form.getValues());
        }
      }}
    >
      {children}
    </form>
  );
};
